import { Button, Checkbox, Form, Input, Select, Spin, Tooltip } from "antd";
import styled from "styled-components";
import Box from "../../components/utility/box";
import { useContext, useEffect, useState, useMemo } from "react";
import {
    SelectOption as Option,
} from '@iso/components/uielements/select';
import api from "../../api";
import noDataImg from '@iso/assets/images/no-data-found.svg';
import noDataPlusIcon from '@iso/assets/images/nodata-plus-icon.svg';
import initialData from "../../redux/scrumBoard/data";
import { AppContext } from "../../context/ContextProvider";
import ContentHolder from '@iso/components/utility/contentHolder';
import { useNavigate } from "react-router-dom";
import { AlertContext } from "../../context/alertContext";
import { hasPermission } from "../../context/helper";
import DeleteTask from "./Tasks/DeleteTask";

const SecretManagerSpinWrapper = styled.div`
  .ant-spin-nested-loading,
  .ant-spin-container {
    min-height: unset !important;
  }
`;




const noDataTitle = {
    color: "#A6A6A6",
    textAlign: "center",
    fontFamily: "Nunito Sans",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "300",
};

const noDataSubTitle = {
    color: "#323232",
    fontFamily: "Poppins",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "400",
    marginLeft: "5px"
};

export default function SecretManagerModal({ selectedAppId, currentProject, setCurrentProject, fromEnvironment, fromWorkflow, workflowName }) {
    const [form] = Form.useForm();
    // const [form1] = Form.useForm();
    const [accountsData, setAccountsData] = useState([]);
    const [selectAccounts, setSelectAccounts] = useState([]);
    const [accountsPageCount, setAccountsPageCount] = useState(1);
    const [doneFetchingAccounts, setDoneFetchingAccounts] = useState(false);
    const [accountsFetching, setAccountsFetching] = useState(false);
    const PAGE_SIZE_ACCOUNTS = 10;
    const [selectRegions, setSelectRegions] = useState([]);
    const [showDeleteDetails, setShowDeleteDetails] = useState(false);
    const { setOpenModel } = useContext(AppContext);
    const { set_alertOptions } = useContext(AlertContext);
    
    // Memoize selected service to avoid repeated lookups or array access
    const selectedService = useMemo(() => {
        console.log("selectedService memoization - selectedAppId:", selectedAppId, "currentProject.tasks:", currentProject?.tasks, "fromEnvironment:", fromEnvironment);
        if (selectedAppId === null || selectedAppId === undefined || !currentProject?.tasks) return null;
        
        if (fromEnvironment) {
            // When fromEnvironment is true, selectedAppId is the array index (serviceIdx)
            const service = currentProject.tasks[selectedAppId];
            console.log("Found service at index", selectedAppId, ":", service);
            return service;
        } else {
            // When fromEnvironment is false, we don't need selectedService for project-level account details
            return null;
        }
    }, [fromEnvironment, currentProject?.tasks, selectedAppId]);

    // Get the correct account details source based on fromEnvironment
    const getAccountDetailsSource = useMemo(() => {
        console.log("getAccountDetailsSource memoization - fromEnvironment:", fromEnvironment, "selectedService:", selectedService, "currentProject.accountDetails:", currentProject?.accountDetails);
        if (fromEnvironment) {
            return selectedService?.accountDetails;
        }
        return currentProject?.accountDetails;
    }, [fromEnvironment, selectedService?.accountDetails, currentProject?.accountDetails]);

    const [accountDetails, setAccountDetails] = useState(() => {
        const source = fromEnvironment 
            ? (currentProject?.tasks?.[selectedAppId]?.accountDetails)
            : currentProject?.accountDetails;
        return typeof source === "string" ? {} : { ...source };
    });
    
    let { regions } = initialData;
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [isSecretNameInvalid, setIsSecretNameInvalid] = useState(false);
    const [showSaveAnyway, setShowSaveAnyway] = useState(false);
    const [checked, setChecked] = useState(false);
    let navigate = useNavigate();

    // Sync accountDetails state when the source changes
    useEffect(() => {
        const source = getAccountDetailsSource;
        if (source && typeof source !== "string") {
            setAccountDetails({ ...source });
        }
    }, [getAccountDetailsSource]);

    // Keep form fields in sync with the local accountDetails state, not with
    // currentProject on every render to avoid overwriting user selections.
    useEffect(() => {
        form.setFieldsValue({ ...accountDetails });
    }, [accountDetails, form]);

    // When modal opens with an already selected cloud account, pre-populate
    // the regions dropdown based on the integration type so the current
    // region renders correctly and can be changed.
    useEffect(() => {
        if (accountDetails?.integration_type) {
            const type = accountDetails.integration_type;
            const myRegions = [];
            regions[type] && Object.keys(regions[type]).map((child) => {
                myRegions.push(
                    <Option key={child} name="region" value={child} data-displayname={regions[type][child].name}>
                        <div style={{ display: "flex", justifyContent: "start", alignItems: "center", width: "100%" }}>
                            <span>{`${regions[type][child].name} / ${child}`}</span>
                        </div>
                    </Option>
                );
            });
            setSelectRegions(myRegions);
        }

        // add region on load
        if(selectRegions?.length <= 0 && accountDetails.integration){
            const type = accountDetails.integration == 3 ? "gcp" : "aws";
            const myRegions = [];
            regions[type] && Object.keys(regions[type]).map((child) => {
                myRegions.push(<Option key={child} name="region" value={child} data-displayname={regions[type][child].name}>
                    <div style={{ display: "flex", justifyContent: "start", alignItems: "center", width: "100%" }}>
                        <span>{`${regions[type][child].name} / ${child}`}</span>
                    </div>
                </Option>);
            });
            setSelectRegions(myRegions);
        }
    }, [accountDetails?.integration_type]);

    useEffect(() => {
        if (accountsPageCount === 1) {
            setSelectAccounts([]);
            setDoneFetchingAccounts(false);
        }
        if (accountsPageCount === 1) setLoading(true);
        setAccountsFetching(true);
        const url = `${process.env.REACT_APP_API_URI}/api/integration/account/list/?from_integrationlist=false&page=1&page_size=1000`;
        api.post(url, { is_active: true })
            .then((res) => {
                let data = res.data;
                const results = data.data?.results ?? [];
                if (data.status) {
                    if (accountsPageCount === 1) setAccountsData(results);

                    const secretManagerData = results.filter(child =>
                        ['secret_manager', 'gcp_secret_manager'].includes(child.integration_type) && child.status
                    );

                    const uniqueAccountIds = [...new Set(secretManagerData
                        .map(child => child.account_id)
                        .filter(accountId => accountId))];

                    let myAccounts = [];
                    uniqueAccountIds.forEach((accountId, ind) => {
                        const firstEntry = secretManagerData.find(child => child.account_id === accountId);
                        if (firstEntry) {
                            myAccounts.push(
                                <Option
                                    key={accountId + "-" + ind}
                                    name="accountId"
                                    data-alias={firstEntry.alias}
                                    type={firstEntry.integration_type}
                                    data-id={firstEntry.id}
                                    data-account-id={accountId}
                                    data-integration={firstEntry.integration}
                                    value={accountId}
                                >
                                    <div style={{ display: "flex", justifyContent: "start", alignItems: "center", width: "100%" }}>
                                        <img src={firstEntry.integration_logo} style={{ width: "18px", height: "18px", marginRight: "10px" }} alt="provider" />
                                        <span>{`${accountId}${firstEntry.alias ? ` (${firstEntry.alias})` : ""}`}</span>
                                    </div>
                                </Option>
                            );
                        }
                    });
                    const redirectOption = <Option key="Integrate New Account" name="redirect">Integrate New Account</Option>;
                    if (results.length < PAGE_SIZE_ACCOUNTS) setDoneFetchingAccounts(true);
                    setSelectAccounts((prev) => {
                        const withoutRedirect = prev.length > 0 && prev[prev.length - 1]?.key === "Integrate New Account" ? prev.slice(0, -1) : prev;
                        return accountsPageCount <= 1 ? (myAccounts.length > 0 ? [...myAccounts, redirectOption] : [redirectOption]) : [...withoutRedirect, ...myAccounts, redirectOption];
                    });
                }
                else {
                    console.log("Error");
                }
            }).catch((err) => {
                console.log("Error");
            }).finally(() => {
                setAccountsFetching(false);
                if (accountsPageCount === 1) setLoading(false);
            });
    }, [accountsPageCount]);

    function toggleAlert(type, message, description) {
        console.log("Toggle alert called: ", type, message, description);
        set_alertOptions({
            status: true,
            type: type,
            message: message,
            description: description,
        });

        // opacity=0 after 10sec
        setTimeout(() => {
            set_alertOptions({
                status: false,
                type: type,
                message: message,
                description: description,
            });
        }, 5 * 1000);
    }

    const handleSave = async () => {
        try {
            await form.validateFields();
            setValidating(true);

            // console.log("accountDetails: ", accountDetails);
            // console.log("showSaveAnyway: ", showSaveAnyway);
            // console.log("fromEnvironment || fromWorkflow: ", fromEnvironment || fromWorkflow);

            // validate secret name if it is not empty
            if ((fromEnvironment || fromWorkflow) && !showSaveAnyway && accountDetails.integration) {
                const payload = {
                    secret_name: accountDetails.secret_name,
                    region: accountDetails.region,
                    account_id: accountDetails.account_id,
                    integration_id: accountDetails.integration
                };
                const validationRes = await api.post(process.env.REACT_APP_API_URI + "/api/integration/secret-manager/validate-secret-name/", payload);
                
                if (validationRes?.data) {
                    if (validationRes.data?.status === false) {
                        let errorMessage = validationRes.data.message;
                        if (validationRes.data?.error_details) {
                            if (typeof validationRes.data.error_details === 'string') {
                                errorMessage = validationRes.data.error_details;
                            } else if (Array.isArray(validationRes.data.error_details)) {
                                errorMessage = validationRes.data.error_details.join(', ');
                            } else if (typeof validationRes.data.error_details === 'object') {
                                errorMessage = Object.values(validationRes.data.error_details).join(', ');
                            }
                        }
                        toggleAlert("error", "Error", errorMessage);
                        setValidating(false);
                        return;
                    }

                    if (validationRes.data.can_create === false) {
                        let msg = validationRes.data.message;
                        if(validationRes.data.message.includes("already exists")){
                            msg = "Secret name already exists. Please enter a different secret name.";
                        }
                        else{
                            msg = validationRes.data.message + ". Please enter a different secret name.";
                        }

                        form.setFields([
                            {
                                name: 'secret_name',
                                errors: [msg],
                            },
                        ]);
                        // toggleAlert("error", "Error", validationRes.data.message);
                        setShowSaveAnyway(true);
                        setIsSecretNameInvalid(true);
                        setValidating(false);
                        return;
                    }
                }
                else{
                    setIsSecretNameInvalid(false);
                }
            }

            setValidating(false);

            if(fromEnvironment){
                // console.log("My current project: ", currentProject);
                // Update the specific task's accountDetails when fromEnvironment
                const updatedTasks = currentProject.tasks.map((task, idx) => {
                    if (idx === selectedAppId) {
                        return {
                            ...task,
                            accountDetails: accountDetails,
                            secret_name: accountDetails.secret_name,
                            // secret_name: `${currentProject.name}-${task.name}`
                        };
                    }
                    return task;
                });
                
                setCurrentProject(currentProject => (
                    {
                        ...currentProject,
                        tasks: updatedTasks
                    }
                ));
                localStorage.setItem("ProjectDep", JSON.stringify({ ...currentProject, tasks: updatedTasks }));
                setOpenModel({
                    modalType: "",
                    modalProps: {
                        data: ""
                    }
                });
            }
            else if(fromWorkflow){
                // console.log("My current project: ", currentProject);
                // Update the specific task's accountDetails when fromWorkflow
                const updatedProject = {
                    ...currentProject,
                    accountDetails: {...accountDetails}
                };
                
                setCurrentProject(updatedProject);
                localStorage.setItem("Project", JSON.stringify({ ...updatedProject }));
                setOpenModel({
                    modalType: "",
                    modalProps: {
                        data: ""
                    }
                });
            }
            else{
                // console.log("Entering else...", currentProject);
                api.patch(process.env.REACT_APP_API_URI + `/api/project_env/project/${currentProject.id}/secret`, {
                    "secret_integration_account": accountDetails?.integration_id?.toString(),
                    "secret_region": accountDetails?.region
                }).then((res) => {
                    let data = res.data;
                    if (data.status) {
                        console.log("My current project: ", currentProject);
                        setCurrentProject(currentProject => (
                            {
                                ...currentProject,
                                accountDetails: accountDetails
                            }
                        ));
                        localStorage.setItem("Project", JSON.stringify({ ...currentProject, accountDetails: accountDetails }));
                        toggleAlert("success", "Success", data.message);
                        setOpenModel({
                            modalType: "",
                            modalProps: {
                                data: ""
                            }
                        });
                    }
                    else {
                        toggleAlert("error", "Error", data?.message);
                        setOpenModel({
                            modalType: "",
                            modalProps: {
                                data: ""
                            }
                        });
                    }
                }).catch((err) => {
                    console.log("Error");
                    toggleAlert("error", "Error", err);
                    setOpenModel({
                        modalType: "",
                        modalProps: {
                            data: ""
                        }
                    });
                })
                // currentProject.tasks[selectedAppId].accountDetails = {...accountDetails};
                // setCurrentProject({
                //     ...currentProject,
                //     tasks: [...currentProject.tasks]
                // });
                // console.log("Saving project: ", currentProject);
                // localStorage.setItem('Project', JSON.stringify({
                //     ...currentProject,
                //     tasks: [...currentProject.tasks]
                //     //accountDetails: accountDetails
                // }));
            }
            console.log("Exiting handleSave...");
        }
        catch (err) {
            console.log("Error logged :", err);
            setValidating(false);
        }
    }

    const handleDisconnect = async () => {
        if(fromEnvironment){
            // Update the specific task's accountDetails and envs when fromEnvironment
            const updatedTasks = currentProject.tasks.map((task, idx) => {
                if (idx === selectedAppId) {
                    //deleting only secrets
                    let filteredEnvs = task.envs;
                    if (task.envs) {
                        filteredEnvs = task.envs.filter((val) => (!val.isSecret /*|| (val.isSecret && val.importFrom !== "Secret Manager")*/));
                    }
                    
                    return {
                        ...task,
                        accountDetails: "Atmosly",
                        secret_name: "",
                        envs: filteredEnvs
                    };
                }
                return task;
            });
            
            setShowDeleteDetails(false);
            setCurrentProject({ ...currentProject, tasks: updatedTasks });
            localStorage.setItem("ProjectDep", JSON.stringify({ ...currentProject, tasks: updatedTasks }));
            // toggleAlert("success", "Success", data.message);
            setOpenModel({
                modalType: "",
                modalProps: {
                    data: ""
                }
            });
        }
        else if(fromWorkflow){
            // Update the specific task's accountDetails and envs when fromWorkflow
            const updatedTasks = currentProject.tasks.map((task, idx) => {
                if (idx === selectedAppId) {
                    //deleting only secrets
                    let filteredEnvs = task.envs;
                    if (task.envs) {
                        filteredEnvs = task.envs.filter((val) => (!val.isSecret /*|| (val.isSecret && val.importFrom !== "Secret Manager")*/));
                    }
                    
                    return {
                        ...task,
                        envs: filteredEnvs
                    };
                }
                return task;
            });

            currentProject.accountDetails = "Atmosly";
            setShowDeleteDetails(false);
            setCurrentProject({ ...currentProject, tasks: updatedTasks });
            localStorage.setItem("Project", JSON.stringify({ ...currentProject, tasks: updatedTasks }));
            // toggleAlert("success", "Success", data.message);
            setOpenModel({
                modalType: "",
                modalProps: {
                    data: ""
                }
            });
        }
        else{
            // await form1.validateFields();
            api.delete(process.env.REACT_APP_API_URI + `/api/project_env/project/${currentProject.id}/secret`).then((res) => {
                let data = res.data;
                if (data.status) {
                    currentProject.accountDetails = "Atmosly";
                    //deleting only secrets
                    // if (selectedService?.envs) {
                    //     let envs = selectedService.envs.filter((val) => (!val.isSecret /*|| (val.isSecret && val.importFrom !== "Secret Manager")*/));
                    //     selectedService.envs = [...envs];
                    // }
                    if (currentProject.tasks?.[selectedAppId]?.envs) {
                        currentProject.tasks.map((item, ind) => {
                            let envs = item?.envs ? item.envs.filter((val) => (!val.isSecret /*|| (val.isSecret && val.importFrom !== "Secret Manager")*/)) : [];
                            currentProject.tasks[ind].envs = [...envs];
                        });
                    }
                    setCurrentProject({ ...currentProject });
                    localStorage.setItem("Project", JSON.stringify({ ...currentProject }));
                    toggleAlert("success", "Success", data.message);
                    setOpenModel({
                        modalType: "",
                        modalProps: {
                            data: ""
                        }
                    });
                }
                else {
                    // currentProject.accountDetails = "Atmosly";
                    toggleAlert("error", "Error", data.message);
                    setOpenModel({
                        modalType: "",
                        modalProps: {
                            data: ""
                        }
                    });
                }
            }).catch((err) => {
                toggleAlert("success", "Success", err);
                setOpenModel({
                    modalType: "",
                    modalProps: {
                        data: ""
                    }
                });
            });
        }
    }

    const handleCancel = async () => {
        console.log("Entering cancel...", currentProject);
        if (!getAccountDetailsSource?.account_id) {
            if (fromEnvironment || fromWorkflow) {
                // For environment level, just close the modal
                setOpenModel({
                    modalType: "",
                    modalProps: {
                        data: ""
                    }
                });
            } else {
                // For project level, make API call
                api.patch(process.env.REACT_APP_API_URI + `/api/project_env/project/${currentProject.id}/secret`, {
                    "is_cancel": true
                }).then((res) => {
                    let data = res.data;
                    if (data.status) {
                        setCurrentProject(currentProject => ({
                            ...currentProject,
                            accountDetails: "Atmosly"
                        }));
                        setOpenModel({
                            modalType: "",
                            modalProps: {
                                data: ""
                            }
                        });
                    }
                    else {
                        toggleAlert("error", "Error", data.message);
                        setOpenModel({
                            modalType: "",
                            modalProps: {
                                data: ""
                            }
                        });
                    }
                }).catch((err) => {
                    console.log("Error");
                    toggleAlert("error", "Error", err);
                    setOpenModel({
                        modalType: "",
                        modalProps: {
                            data: ""
                        }
                    });
                });
            }
        }
        else {
            setOpenModel({
                modalType: "",
                modalProps: {
                    data: ""
                }
            });
        }
    }

    const handleAccountsPopupScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (!doneFetchingAccounts && (scrollHeight - scrollTop <= clientHeight + 10) && !accountsFetching) {
            setAccountsPageCount((prev) => prev + 1);
            setAccountsFetching(true);
        }
    };

    const handleSelectChange = (value, event) => {
        let myRegions = [];
        console.log("value, event :", value, event);
        if (event.name === "accountId") {
            let type = event["type"];
            let accountId = event["data-account-id"];
            console.log("value in handleSelectChange :", value);
            console.log("type in handleSelectChange :", type);
            console.log("accountId in handleSelectChange :", accountId);
            
            if(type === "secret_manager" || type === "gcp_secret_manager"){
                // For secret_manager and gcp_secret_manager, filter regions based on BOTH account_id AND integration_type
                // This ensures we only show regions for the specific account and type combination
                const secretManagerData = accountsData.filter(child => 
                    child.integration_type === type && 
                    child.status && 
                    child.account_id === accountId
                );
                
                // Get unique regions for this specific account_id and type combination
                const accountRegions = [...new Set(secretManagerData
                    .map(child => child.region)
                    .filter(region => region))];
                
                accountRegions.forEach((region, ind) => {
                    myRegions.push(<Option key={region + "-" + ind} name="region" value={region}>
                        <div style={{ display: "flex", justifyContent: "start", alignItems: "center", width: "100%" }}>
                            <span>{region}</span>
                        </div>
                    </Option>);
                });
            } else {
                regions[type] && Object.keys(regions[type]).map((child, ind) => {
                    myRegions.push(<Option key={child} name="region" value={child} data-displayname={regions[type][child].name}>
                        <div style={{ display: "flex", justifyContent: "start", alignItems: "center", width: "100%" }}>
                            <span>{`${regions[type][child].name} / ${child}`}</span>
                            {/* <span>{child}</span> */}
                        </div>
                    </Option>);
                });
            }
            
            setAccountDetails((prev) => ({
                ...prev,
                // integration_id: value,
                account_id: accountId,
                alias: event["alias"],
                integration_type: event["integration_type"],
                integration_id: event["data-id"],
                integration: event["data-integration"],
                region: "",
                secret_name: ""
            }));

            form.setFieldsValue({
                account_id: value,
                region: "",
                secret_name: "",
            });
            setSelectRegions(myRegions);
            setShowSaveAnyway(false);
        }
        else if (event.name === "redirect") {
            window.open("/integrations", "_blank");
        }
        else if (event.name === "ATMOSLY") {
            // setCurrentProject(currentProject => ({
            //     ...currentProject,
            //     accountDetails: "Atmosly"
            // }));
            setAccountDetails({
                ...accountDetails,
                account_id: "ATMOSLY",
                alias: "",
                integration_type: "",
                integration_id: "",
                integration: "",
                region: "",
                secret_name: "",
            });
            form.setFieldsValue({
                account_id: "ATMOSLY",
                region: "",
                secret_name: "",
            });
            setSelectRegions([]);
            setShowSaveAnyway(false);
        } 
        else if (event.name === "region") {
            setAccountDetails((prev) => { 
                let tempData = {
                    ...prev,
                    region: value
                };

                // console.log("tempData in handleSelectChange :", tempData);
                // form.setFieldsValue({...tempData});

                return tempData;
            });
            form.setFieldsValue({ region: value });
            setShowSaveAnyway(false);
        }

        setIsSecretNameInvalid(false);
        form.setFields([
            {
                name: 'secret_name',
                errors: [],
            },
        ]);
    }
    
    const handleChange = (e) => {
        console.log("e in handleClick :", e.target.name);
        
        if(e.target.name === "secret_name"){
            let val = e.target?.value?.trim() || e.target.value;

            setAccountDetails({
                ...accountDetails,
                secret_name: val
            });

            form.setFieldsValue({ secret_name: val });
            setShowSaveAnyway(false);
            setIsSecretNameInvalid(false);
        }
    }

    return (
        <SecretManagerSpinWrapper>
        <Spin spinning={validating}>
            <div>
                <Form form={form}>
                {console.log("Account id: ", accountDetails.account_id)}
                <Box
                    style={{ border: "0", margin: "0", paddingBottom: "0" }}
                    // title="Select your provider to manage your secrets"
                    title="Select your secret manager to manage your secrets"
                    // subtitle="Select your Provider Account to proceed"
                    titleStyle={{
                        flexGrow: "0",
                        fontFamily: "Poppins",
                        fontSize: "14px",
                        fontWeight: "500",
                        fontStretch: "normal",
                        fontStyle: "normal",
                        lineHeight: "1.25",
                        letterSpacing: "normal",
                        textAlign: "left",
                        color: "#1f1f1f",
                        marginBottom: "6px"
                    }}
                    subTitleStyle={{
                        flexGrow: 0,
                        fontFamily: "Nunito Sans",
                        fontSize: "14px",
                        fontWeight: "normal",
                        fontStretch: "normal",
                        fontStyle: "normal",
                        lineHeight: "1.43",
                        letterSpacing: "normal",
                        textAlign: "left",
                        color: "#9b9b9b",
                    }}
                >
                    <Form.Item 
                        name="account_id"
                        // style={{ marginBottom: "15px" }}
                        rules={[
                            {
                                required: true,
                                message: "Please select secret manager account."
                            }
                        ]}
                    >
                        <Select
                            value={accountDetails.account_id}
                            onChange={handleSelectChange}
                            style={{ width: "100%" }}
                            notFoundContent={loading ? <Spin size="small" /> : "No Data Found"}
                            disabled={getAccountDetailsSource?.account_id ? true : false}
                            onPopupScroll={handleAccountsPopupScroll}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            dropdownRender={(menu) => (
                                <div>
                                    {selectAccounts.length === 0 && accountsFetching ? (
                                        <div style={{ textAlign: 'center', padding: 8 }}>
                                            <Spin size="small" />
                                        </div>
                                    ) : (menu)}
                                    {!doneFetchingAccounts && selectAccounts.length > 0 && accountsFetching && (
                                        <div style={{ textAlign: 'center', padding: 6 }}>
                                            <Spin size="small" />
                                        </div>
                                    )}
                                </div>
                            )}
                        >
                            {selectAccounts.length > 0 ? selectAccounts :
                                !accountsFetching ? (
                                    <Option key="0" name="account-redirect" data-alias="" type="integration" style={{ background: "white" }}>
                                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "center", height: "fit-content", padding: "3px 0" }}>
                                            <img src={noDataImg} />
                                            <span style={noDataTitle}>No Account available.</span>
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                <img src={noDataPlusIcon} />
                                                <span style={noDataSubTitle}>Create one</span>
                                            </div>
                                        </div>
                                    </Option>
                                ) : (
                                    <Option key="accounts-loader">
                                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40px" }}>
                                            <Spin size="small" />
                                        </div>
                                    </Option>
                                )
                            }
                        </Select>
                    </Form.Item>
                </Box>
                {accountDetails !== "ATMOSLY" && accountDetails.account_id && accountDetails.account_id !== "ATMOSLY" && <>
                    <Box
                        style={{ border: "0", margin: "0", paddingTop: "0", paddingBottom: "0" }}
                        title="Select region"
                        // subtitle="Select Region"
                        titleStyle={{
                            flexGrow: "0",
                            fontFamily: "Poppins",
                            fontSize: "14px",
                            fontWeight: "500",
                            fontStretch: "normal",
                            fontStyle: "normal",
                            lineHeight: "1.25",
                            letterSpacing: "normal",
                            textAlign: "left",
                            color: "#1f1f1f",
                            marginBottom: "6px"
                        }}
                        subTitleStyle={{
                            flexGrow: 0,
                            fontFamily: "Nunito Sans",
                            fontSize: "14px",
                            fontWeight: "normal",
                            fontStretch: "normal",
                            fontStyle: "normal",
                            lineHeight: "1.43",
                            letterSpacing: "normal",
                            textAlign: "left",
                            color: "#9b9b9b",
                        }}
                    >
                        <Form.Item
                            name="region" 
                            // style={{ marginBottom: "15px" }}
                            rules={[
                                {
                                    required: true,
                                    message: "Please select a region."
                                }
                            ]}
                        >
                            <Select
                                value={accountDetails.region}
                                onChange={handleSelectChange}
                                style={{ width: "100%" }}
                                disabled={getAccountDetailsSource?.account_id ? true : false}
                            >   {/*, marginBottom: fromEnvironment ? "20px": "0" */}
                                {selectRegions}
                            </Select>
                        </Form.Item>
                    </Box>
                </>}
                
                {(fromWorkflow || fromEnvironment) && accountDetails?.region && <>
                    <Box
                        style={{ border: "0", margin: "0", paddingTop: "0", paddingBottom: "0" }}
                        title="Secret Name"
                        titleStyle={{
                            flexGrow: "0",
                            fontFamily: "Poppins",
                            fontSize: "14px",
                            fontWeight: "500",
                            fontStretch: "normal",
                            fontStyle: "normal",
                            lineHeight: "1.25",
                            letterSpacing: "normal",
                            textAlign: "left",
                            color: "#1f1f1f",
                            marginBottom: "6px"
                        }}
                        subTitleStyle={{
                            flexGrow: 0,
                            fontFamily: "Nunito Sans",
                            fontSize: "14px",
                            fontWeight: "normal",
                            fontStretch: "normal",
                            fontStyle: "normal",
                            lineHeight: "1.43",
                            letterSpacing: "normal",
                            textAlign: "left",
                            color: "#9b9b9b",
                        }}
                    >
                        <Form.Item name="secret_name" rules={[
                            {
                                required: true,
                                message: "Please enter secret name."
                            },
                            {
                                // Custom validator for AWS and GCP Secret Manager naming conventions
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.resolve();
                                    }
                                    
                                    const trimmedValue = value.trim();
                                    
                                    // Check if empty after trimming
                                    if (trimmedValue.length === 0) {
                                        return Promise.reject(new Error('Secret name cannot be empty.'));
                                    }
                                    
                                    // Determine provider type
                                    const integrationType = accountDetails?.integration;
                                    const isAWS = integrationType === 1;
                                    const isGCP = integrationType === 3;
                                    
                                    // Apply provider-specific validations
                                    if (isAWS) {
                                        // AWS Secrets Manager validation
                                        // Max length: 512 characters
                                        if (trimmedValue.length > 512) {
                                            return Promise.reject(new Error('Secret name cannot exceed 512 characters.'));
                                        }
                                        
                                        // AWS allows: letters, digits, and /_+=.@-
                                        const awsSecretNamePattern = /^[a-zA-Z0-9/_+=.@-]+$/;
                                        
                                        if (!awsSecretNamePattern.test(trimmedValue)) {
                                            return Promise.reject(new Error('Secret name can only contain letters, numbers, and these special characters: /_+=.@-'));
                                        }
                                        
                                        // Cannot start or end with a slash
                                        if (trimmedValue.startsWith('/') || trimmedValue.endsWith('/')) {
                                            return Promise.reject(new Error('Secret name cannot start or end with a forward slash (/).'));
                                        }
                                        
                                        // Cannot contain consecutive slashes
                                        if (trimmedValue.includes('//')) {
                                            return Promise.reject(new Error('Secret name cannot contain consecutive forward slashes (//).'));
                                        }
                                        
                                        // Cannot be just "." or ".."
                                        if (trimmedValue === '.' || trimmedValue === '..') {
                                            return Promise.reject(new Error('Secret name cannot be "." or "..".'));
                                        }
                                    } else if (isGCP) {
                                        // GCP Secret Manager validation
                                        // Max length: 255 characters
                                        if (trimmedValue.length > 255) {
                                            return Promise.reject(new Error('Secret name cannot exceed 255 characters.'));
                                        }
                                        
                                        // GCP allows: letters, numbers, hyphens, underscores
                                        // Must start with a letter
                                        const gcpSecretNamePattern = /^[a-zA-Z0-9_-]+$/;
                                        
                                        if (!gcpSecretNamePattern.test(trimmedValue)) {
                                            return Promise.reject(new Error('Secret name can only contain letters, numbers, hyphens (-), and underscores (_).'));
                                        }
                                        
                                        // Must start with a letter (GCP requirement)
                                        if (!/^[a-zA-Z]/.test(trimmedValue)) {
                                            return Promise.reject(new Error('Secret name must start with a letter.'));
                                        }
                                    } else {
                                        // Fallback: Apply most restrictive common rules (GCP-like) if provider not yet selected
                                        if (trimmedValue.length > 255) {
                                            return Promise.reject(new Error('Secret name cannot exceed 255 characters.'));
                                        }
                                        
                                        // Use GCP pattern as more restrictive common denominator
                                        const commonSecretNamePattern = /^[a-zA-Z0-9_-]+$/;
                                        
                                        if (!commonSecretNamePattern.test(trimmedValue)) {
                                            return Promise.reject(new Error('Secret name can only contain letters, numbers, hyphens (-), and underscores (_).'));
                                        }
                                        
                                        // Must start with a letter (GCP requirement, safe for AWS too)
                                        if (!/^[a-zA-Z]/.test(trimmedValue)) {
                                            return Promise.reject(new Error('Secret name must start with a letter.'));
                                        }
                                    }
                                    
                                    return Promise.resolve();
                                }
                            }
                        ]}>
                            <Input
                                name="secret_name"
                                value={accountDetails.secret_name}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                                placeholder="Enter Secret Name"
                                disabled={getAccountDetailsSource?.account_id ? true : false}
                            />
                        </Form.Item>
                    </Box>
                </>}

                {!getAccountDetailsSource?.account_id && <Box style={{ border: "0", paddingTop: "0px", paddingBottom: "0" }}>
                    <p className="isoWarningText" style={{ margin: "0", padding: "15px 0px" }}>
                        <span>Note: Once you select an account provider, this choice cannot be changed. Canceling will set the provider to Atmosly by default, but you can change this selection later.</span>
                    </p>
                </Box>}
            </Form>
            {/* <Form name="disconnectCheck" form={form1}>
            {getAccountDetailsSource?.account_id && <Box style={{ border: "0", paddingTop: "0", paddingBottom: "0" }}>
                <Form.Item name="accountTerms" rules={[{
                    required: !checked,
                    message: "Please check this box to proceed."
                }]}>
                <Checkbox name="accountTerms" checked={checked} onChange={(e) => {
                    setChecked(e.target.checked);
                }}> This action will disconnect your account and delete all related data. Are you sure you want to continue?</Checkbox>
                </Form.Item>
            </Box>}
            </Form> */}
            {console.log("showDeleteDetails: ", showDeleteDetails)}
            {console.log("showDeleteDetails: ", ((fromEnvironment || fromWorkflow) && showDeleteDetails))}
            {(((fromEnvironment || fromWorkflow) && showDeleteDetails) || (!fromEnvironment && !fromWorkflow)) && <div style={{ padding: getAccountDetailsSource?.account_id ? "20px" : "0px" }}>
                {getAccountDetailsSource?.account_id && <Box style={{ borderRadius: "10px" }}>
                    <DeleteTask title={"Disconnect this account permanently?"} data={{ ...accountDetails, title: "Disconnect this account permanently?", name: getAccountDetailsSource?.secret_name || "Disconnect" }} subTitle3={"PLEASE NOTE: Disconnecting this account will not retrieve any Secrets, and all existing data will remain securely stored in the Secret Manager."} onDelete={handleDisconnect} fromEnvironment={fromEnvironment} fromWorkflow={fromWorkflow} setShowDeleteDetails={setShowDeleteDetails} />
                </Box>}
            </div>}

            {(getAccountDetailsSource === "Atmosly" || !getAccountDetailsSource?.account_id) && <ContentHolder style={{ width: "100%", display: "flex", justifyContent: 'center', alignItems: 'center' }}>
                <Button className="customButtonActive" style={{ marginRight: "20px", width: "160px", borderColor: "#132f44", color: "#132f44" }} onClick={handleCancel}>
                    {"Cancel"}
                </Button>
                <Button className="customButtonActive" disabled={(!getAccountDetailsSource?.account_id) && isSecretNameInvalid} loading={(!getAccountDetailsSource?.account_id) ? validating : false} style={(!getAccountDetailsSource?.account_id) ? { background: "#132F44", borderRadius: "8px", color: "#FFFFFF", width: "200px", opacity: ((!getAccountDetailsSource?.account_id) && isSecretNameInvalid) ? "0.5" : "1" } : { borderColor: "#CB444A", background: "#CB444A", color: "white", marginRight: "10px", height: "32px", width: "200px" }}
                    onClick={(!getAccountDetailsSource?.account_id) ? handleSave : handleDisconnect}>
                    {/* {(!getAccountDetailsSource?.account_id) ? (showSaveAnyway ? "Save Anyway" : "Confirm") : "Disconnect"} */}
                    {(!getAccountDetailsSource?.account_id) ? "Confirm" : "Disconnect"}
                </Button>
            </ContentHolder>}

            {!showDeleteDetails && getAccountDetailsSource?.account_id && (fromEnvironment || fromWorkflow) && <ContentHolder style={{ width: "100%", display: "flex", justifyContent: 'end', alignItems: 'center' }}>
                {/* <Tooltip title={getAccountDetailsSource?.secret_name === accountDetails.secret_name ? "Secret name is same as existing. Please enter a different secret name." : ""}>
                    <Button className="customButtonActive" disabled={getAccountDetailsSource?.secret_name === accountDetails.secret_name} style={{ background: "#132F44", borderRadius: "8px", color: "#FFFFFF", width: "100px", marginRight: "10px", opacity: getAccountDetailsSource?.secret_name === accountDetails.secret_name ? "0.5" : "1" }} onClick={handleSave}>
                        Update
                    </Button>
                </Tooltip> */}
                <Button className="customButtonActive" style={(!getAccountDetailsSource?.account_id) ? { background: "#132F44", borderRadius: "8px", color: "#FFFFFF", width: "200px" } : { borderColor: "#CB444A", background: "#CB444A", color: "white", marginRight: "10px", height: "32px", width: "120px" }} onClick={async() => {
                    if(showDeleteDetails) {
                        handleDisconnect();
                    }
                    else {
                        setShowDeleteDetails(true);
                    }
                }}>
                    Disconnect
                </Button>
            </ContentHolder>}
            </div>
        </Spin>
        </SecretManagerSpinWrapper>
    );
}